import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, ListGroup, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Protocols } from '../../api/Protocols';
import AppLoader from '../../components/AppLoader';
import AppSearch from '../../components/AppSearch';
import IconButton from '../../components/IconButton';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { ImportableProtocol, Protocol, ProtocolImportDTO } from '../../models/Protocol';
import './ProtocolList.scss';
import { UserRole } from '../../models/User';
import { useAuth } from '../../contexts/auth';
import { format, parseISO } from 'date-fns';

export default function TenantProtocolList() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [protocols, setProtocols] = useState<Protocol[] | null>();
    const [importableProtocols, setImportableProtocols] = useState<ImportableProtocol[] | null>();

    const [validated, setValidated] = useState<boolean>(false);

    const [protocolToDelete, setProtocolToDelete] = useState<Protocol>();
    const [searchText, setSearchText] = useState<string>("");

    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

    const [showImportModal, setShowImportModal] = useState<boolean>(false);

    const { loggedUser } = useAuth()

    const { showLoader, hideLoader } = useFullScreenLoader();
    const { showToast } = useToasts();

    useEffect(() => {
        initProtocols();
        initImportableProtocols();
        // eslint-disable-next-line
    }, []);

    const handleDeleteModalClose = () => setShowDeleteModal(false);

    const openDeleteModal = (protocol: Protocol) => () => {
        setShowDeleteModal(true);
        setProtocolToDelete(protocol);
    }

    const reset = () => {
        //setProtocols(null);
        initProtocols();
    }

    const openImportModal = () => setShowImportModal(true);
    const handleImportModalClose = () => setShowImportModal(false);

    const handleImportSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
        const form = evt.currentTarget;
        evt.stopPropagation();
        evt.preventDefault();

        if (form.checkValidity() === true) {
            showLoader!();

            let protocolImportDTO: ProtocolImportDTO[] = [{
                type: "protocols",
                ids: importableProtocols!.filter(iP => iP.checked).map(iP => String(iP.object.id))
            }
            ]

            Protocols.importProtocols(protocolImportDTO)
                .then(
                    () => {
                        hideLoader!();
                        showToast!('protocol-success', `Protocol imported successfully`, 'Success', 'success');
                        handleImportModalClose();
                        reset();
                    },
                    (err) => {
                        const message = err.response?.data?.message || err.message;
                        const status = err.response?.status;
                        hideLoader!();
                        showToast!('protocol-error', message, `Protocols Error ${status ? `(${status})` : ''}`, 'danger');
                    }
                )

        }
    }

    const handleImportProtocolCheck = (protocolIndex: number) => (evt: React.ChangeEvent<any>) => {

        const value = evt.target.checked;

        let updatedImportedProtocols = [...importableProtocols!]
        updatedImportedProtocols[protocolIndex].checked = value;

        setImportableProtocols([...updatedImportedProtocols])

    }

    const handleCheckedProtocol = (protocolIndex: number) => (evt: React.ChangeEvent<any>) => {

        const value = evt.target.checked;

        let updatedProtocols = [...protocols!]
        updatedProtocols[protocolIndex].checked = value;

        setProtocols([...updatedProtocols])

    }

    const handleSearchChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const value = evt.target.value;
        setSearchText(value);
    }

    const deleteProtocol = () => {
        showLoader!();

        Protocols.deleteTenantProtocol(protocolToDelete!.id!)
            .then(
                () => {
                    hideLoader!();
                    showToast!('protocol-success', 'Protocol deleted successfully', 'Delete Success', 'success');
                    handleDeleteModalClose();
                    reset();
                },
                (err) => {
                    const message = err.response?.data?.message || err.message;
                    const status = err.response?.status;
                    hideLoader!();
                    showToast!('protocol-error', message, `Protocols Error ${status ? `(${status})` : ''}`, 'danger');
                }
            )
    }

    const initProtocols = () => {
        Protocols.getTenantProtocols()
            .then(
                ({ data: protocols }) => {
                    protocols.map((p) => { p.checked = false; return p });
                    setProtocols(protocols)
                },
                (err) => {
                    const message = err.response?.data?.message || err.message;
                    const status = err.response?.status;
                    hideLoader!();
                    showToast!('protocols-error', message, `Protocols Error ${status ? `(${status})` : ''}`, 'danger');
                }
            );
    }

    const initImportableProtocols = () => {
        Protocols.getImportableProtocols()
            .then(
                ({ data: protocols }) => {
                    protocols.map((p) => { p.checked = false; return p });
                    setImportableProtocols(protocols);
                },
                (err) => {
                    const message = err.response?.data?.message || err.message;
                    const status = err.response?.status;
                    hideLoader!();
                    showToast!('protocols-error', message, `Protocols Error ${status ? `(${status})` : ''}`, 'danger');
                }
            );
    }



    const renderProtocols = () => {
        const filteredProtocols = protocols!.filter(protocol => protocol.name.toLowerCase().includes(searchText.toLowerCase())).filter((prot) => !prot.deleted);

        return (
            <>
                <Row className="gx-2 mb-3">
                    <Col>
                        <AppSearch value={searchText} onChange={handleSearchChange} placeholder={t('protocolsList.search')} />
                    </Col>
                </Row>

                {filteredProtocols.length === 0 && (
                    <Alert className="mb-0 py-2">
                        <FontAwesomeIcon icon={solid('info-circle')} />
                        <span className="ms-2">{t('protocolsList.noProtocols')}</span>
                    </Alert>
                )}

                {protocols!.filter(p => p.checked).length > 0 &&
                    <Alert className="mb-2 py-2">
                        <FontAwesomeIcon icon={solid('info-circle')} />
                        <span className="ms-2">Protocolli selezionati: {protocols!.filter(p => p.checked).length}</span>
                    </Alert>
                }

                <ListGroup>

                    <ListGroup.Item key="0" className="align-items-center">
                        <Row>
                            <Col md={4} className="">Protocols</Col>
                            <Col md={3} className="ps-0">Created At:</Col>
                            <Col md={3} className="ps-0">Updated At:</Col>
                            <Col md={2} className="text-end">Actions</Col>
                        </Row>

                    </ListGroup.Item>
                    {filteredProtocols.map((protocol, index) => (
                        <ListGroup.Item key={protocol.id} className="d-flex align-items-center">
                            <Col md={4} className='overflow-hidden d-flex align-items-center'>
                                <div className="pe-3">

                                    <FontAwesomeIcon icon={solid('calendar-check')} size="lg" fixedWidth />

                                    {/* TODO: enable after bulk delete
                                    <Form.Check
                                    inline
                                    className='ms-3'
                                    id={String(protocol.id)}
                                    type="checkbox"
                                    checked={protocol.checked}
                                    onChange={handleCheckedProtocol(index)}
                                    /> 
                                */}
                                </div>

                                <Col className="overflow-hidden">
                                    <a className="mb-0" onClick={() => navigate(`/protocols/${protocol.id}/details`)}> {protocol.name}</a>
                                    <p className="text-truncate mb-0">{protocol.description}</p>
                                </Col>
                            </Col>


                            <Col md={3}>
                                <Badge bg="light" text="dark" className="border justify-self-end">
                                    <span className="">{format(parseISO(protocol.createdAt!), 'dd/MM/yyyy HH:mm')}</span>
                                </Badge>
                            </Col>

                            <Col md={3}>
                                <Badge bg="light" text="dark" className="border justify-self-end">
                                    <span className="">{format(parseISO(protocol.updatedAt!), 'dd/MM/yyyy HH:mm')}</span>
                                </Badge>
                            </Col>

                            <Col md={2} className="ps-3 text-end">
                                {loggedUser!?.role === UserRole.ADMIN &&
                                    <IconButton className="ms-2" onClick={openDeleteModal(protocol)}>
                                        <FontAwesomeIcon className="text-danger" icon={solid('trash')} />
                                    </IconButton>}
                            </Col>
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            </>
        )
    }

    const renderAsyncContent = () => {
        return protocols
            ? renderProtocols()
            : (
                <div className="d-flex justify-content-center py-5">
                    <AppLoader size={150} />
                </div>
            )
    }

    return (
        <Container>
            <Card>
                <div className="d-flex align-items-center justify-content-between border-bottom py-2 px-3">
                    <Card.Title className="mb-0">{t('protocolsList.title')}</Card.Title>

                    <div>{
                        loggedUser!?.role === UserRole.ADMIN &&
                        <Button variant="light" size="sm" onClick={openImportModal} disabled={importableProtocols === undefined ? true : false}>
                            <FontAwesomeIcon icon={solid('cloud-arrow-down')} />
                            <span className="ms-2">{t('protocolsList.import')}</span>
                        </Button>
                    }
                    </div>
                </div>

                <Card.Body>{renderAsyncContent()}</Card.Body>
            </Card>

            <Modal show={showDeleteModal} onHide={handleDeleteModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('common.deleteTitle', { item: t('common.protocol') })}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {t('protocolsList.deleteMessage')} <b>{protocolToDelete?.name}</b>?

                    <h5 className="my-4 text-center text-danger">{t('common.deleteWarning')}</h5>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="light" className="border" size="sm" onClick={handleDeleteModalClose}>{t('common.cancel')}</Button>
                    <Button variant="danger" size="sm" onClick={deleteProtocol}>{t('common.delete')}</Button>
                </Modal.Footer>
            </Modal>


            <Modal show={showImportModal} onHide={handleImportModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('common.import')} {t('common.protocol')}</Modal.Title>
                </Modal.Header>

                <Form validated={validated} noValidate onSubmit={handleImportSubmit}>
                    <Modal.Body>
                        <ListGroup>
                            {importableProtocols ? importableProtocols!.map((impProtocol, index) => (
                                <ListGroup.Item key={impProtocol.object.id} className="d-flex align-items-center">
                                    <div className="pe-3">
                                        <FontAwesomeIcon icon={solid('calendar-check')} size="lg" fixedWidth />
                                    </div>

                                    <Col className="overflow-hidden">
                                        <h6 className="mb-0">{impProtocol.object.name}</h6>
                                    </Col>

                                    <div className="ps-3 text-end">
                                        <Form.Check
                                            inline
                                            className='ms-3'
                                            id={String(impProtocol.object.id)}
                                            type="checkbox"
                                            checked={impProtocol.checked}
                                            onChange={handleImportProtocolCheck(index)}
                                        />
                                    </div>
                                </ListGroup.Item>
                            )) : <></>}
                        </ListGroup>

                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="light" className="border" size="sm" onClick={handleImportModalClose}>{t('common.cancel')}</Button>
                        <Button variant="success" size="sm" type="submit">{t('common.confirm')}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    )
}